import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Clock, Loader2, MapPin } from "lucide-react";
import { formatDayDate, formatTimeRange } from "../lib/datetime";
import { getRole } from "../lib/auth";
import { teacherPDDayPath } from "../lib/pdDayRoutes";
import { preparePDDay } from "../pdday/usePDDay";
import { prepareTeacherPDDaySummary } from "./useTeacherPDDaySummary";
import type { PDDaySummary } from "../types";

export type PDDayGroup = "active" | "past" | "future";

// Future sessions follow the teacher app's locked-module pattern: a real
// but disabled button — visible, dimmed, no navigation on tap — rather
// than a link that silently does nothing.
export function PDDayRow({
  day,
  group,
  dayNumber,
}: {
  day: PDDaySummary;
  group: PDDayGroup;
  dayNumber?: number;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const future = group === "future";
  // The live control screen behind a row (roster, start/end, attendance) is
  // facilitator-only, so a teacher never opens it. A past day opens their
  // own read-only summary; today's opens Home, where their current module
  // actually lives and is unlocked once the PD Day's scheduled window has
  // started — this card just gets them there, it isn't the activity screen
  // itself.
  const isTeacher = getRole() === "teacher";
  // groupPDDays files a day as past once its scheduled end has passed.
  const finished = group === "past";
  const disabled = future;
  const navigable = !future;

  const target = isTeacher ? teacherPDDayPath(day, finished) : `/pd-day/${day.id}`;

  const chip = finished
    ? "bg-danger-bg text-archive-ink"
    : day.status === "in_progress"
      ? "bg-success-bg text-success-text"
      : future
        ? "bg-warning-bg text-warning-text"
        : "bg-accent-soft text-accent";

  // Finished days carry a muted red wash so the archive reads at a glance;
  // archive-tint is far lighter than accent-strong, which stays reserved
  // for actions that end or destroy something.
  const card = finished ? "bg-archive-tint" : "bg-surface";

  const chipLabel = finished
    ? t("statusEnded")
    : day.status === "in_progress"
      ? t("statusInProgress")
      : future
        ? t("statusUpcoming")
        : t("statusReady");

  async function openDay() {
    if (opening || disabled) return;
    if (!isTeacher || (finished && !day.modules?.length)) {
      setOpening(true);
      if (isTeacher) await prepareTeacherPDDaySummary(day.id);
      else await preparePDDay(day.id);
      if (!mounted.current) return;
    }
    navigate(target);
  }

  return (
    <button
      type="button"
      className={`flex w-full items-center gap-2 rounded-xl p-4 text-start shadow-raised transition-shadow ${navigable ? "hover:shadow-card" : "cursor-default"} disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:shadow-raised ${card}`}
      onClick={navigable ? () => void openDay() : undefined}
      disabled={disabled || opening}
      aria-disabled={disabled || opening || !navigable}
      aria-busy={opening}
    >
      <span className="grid flex-1 gap-2">
        <span
          className={`w-fit rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${chip}`}
        >
          {chipLabel}
        </span>

        <strong className="text-lg font-bold text-app-ink">
          {dayNumber ? t("pdDayOrdinal", { number: dayNumber }) : t("pdDay")}
        </strong>
        <span className="text-sm text-app-muted">{formatDayDate(day.start, i18n.language)}</span>

        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-app-muted">
          <span className="inline-flex items-center gap-1" dir="ltr">
            <Clock size={14} />
            {formatTimeRange(day.start, day.end, i18n.language)}
          </span>
        </span>

        <span className="inline-flex items-center gap-1 text-sm text-app-copy">
          <MapPin size={14} className="shrink-0 text-app-icon" />
          <span className="truncate">{day.center}</span>
        </span>

        {future && <span className="text-xs text-app-muted">{t("futureNotOpenYet")}</span>}
      </span>

      {navigable &&
        (opening ? (
          <Loader2 size={18} className="shrink-0 animate-spin text-app-icon" />
        ) : (
          <ChevronRight size={18} className="shrink-0 text-app-icon rtl:rotate-180" />
        ))}
    </button>
  );
}
